import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Alert,
  Chip
} from '@mui/material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { DATABASE } from '../../../../Config/firebase';
import { calculateDailyBalance, formatDailyBalanceSummary } from '../../../../Utils/dailyBalanceCalculator';
import { getCommissionRate } from '../../../../Utils/fundRules';
import dayjs from 'dayjs';

const PerformancePreview = () => {
  const [email, setEmail] = useState('');
  const [month, setMonth] = useState(dayjs().subtract(1, 'month').format('YYYY-MM'));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [userData, setUserData] = useState(null);

  const handlePreview = async () => {
    if (!email) {
      alert('Please enter an email');
      return;
    }

    setLoading(true);
    try {
      // Find user
      const usersQuery = query(
        collection(DATABASE, 'users'),
        where('email', '==', email.trim())
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        alert('User not found');
        setLoading(false);
        return;
      }

      const userDoc = usersSnapshot.docs[0];
      const userId = userDoc.id;
      const user = userDoc.data();
      setUserData({ id: userId, ...user });

      // Get ledgers for the selected month
      const monthStart = dayjs(month).startOf('month');
      const monthEnd = dayjs(month).endOf('month');

      const ledgersQuery = query(
        collection(DATABASE, 'ledgers'),
        where('user_id', '==', userId),
        orderBy('created_at', 'asc')
      );
      const ledgersSnapshot = await getDocs(ledgersQuery);
      const allLedgers = ledgersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter ledgers for this month (deposits/withdrawals that affect the month)
      const monthLedgers = allLedgers.filter(ledger => {
        const ledgerDate = ledger.date?.toDate?.() || ledger.created_at?.toDate?.();
        if (!ledgerDate) return false;
        
        const ledgerDay = dayjs(ledgerDate);
        // Include events from last day of previous month (D+1 deposits) to last day of current month
        return ledgerDay.isSameOrAfter(monthStart.subtract(1, 'day')) && ledgerDay.isSameOrBefore(monthEnd);
      });

      console.log('Found ledgers for month:', monthLedgers);

      // For demo purposes, use sample daily percentages (you can make this configurable)
      // In real use, this would come from the "Add Monthly" form
      const daysInMonth = monthEnd.date();
      const sampleDailyPercentages = Array(daysInMonth).fill(0.59); // Example: 0.59% per day

      // Get commission rate from fund_rules (for positive performance)
      // The function will use 0% for negative performance automatically
      const commissionRate = await getCommissionRate(1, null); // Pass 1 (positive) to get positive rate

      // Calculate using new logic with commission rate from fund_rules
      const calculationResult = calculateDailyBalance({
        openingBalance: user.net_balance || 0,
        ledgers: monthLedgers,
        dailyPercentages: sampleDailyPercentages,
        monthStart,
        monthEnd,
        userId,
        commissionRate // Pass commission rate from fund_rules
      });

      console.log(formatDailyBalanceSummary(calculationResult));
      setResult(calculationResult);

    } catch (error) {
      console.error('Error previewing performance:', error);
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Performance Preview Tool
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Preview daily balance calculation for any user/month to validate the new performance logic.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="User Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handlePreview}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? 'Loading...' : 'Preview Calculation'}
              </Button>
            </Grid>
          </Grid>

          {userData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{userData.full_name || userData.name}</strong> • 
                Current Balance: ${(userData.net_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {result && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Calculation Summary
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Opening Balance</Typography>
                  <Typography variant="h6">${result.openingBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Gross Profit</Typography>
                  <Typography variant="h6" color={result.totalGrossProfit >= 0 ? 'success.main' : 'error.main'}>
                    ${result.totalGrossProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Commission ({result.commissionRate}%)</Typography>
                  <Typography variant="h6">-${result.commission.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Net Profit</Typography>
                  <Typography variant="h6" color={result.netProfit >= 0 ? 'success.main' : 'error.main'}>
                    ${result.netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Final Balance</Typography>
                  <Typography variant="h6">${result.finalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Gross %</Typography>
                  <Typography variant="h6">{result.grossPerformancePercentage.toFixed(6)}%</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Net %</Typography>
                  <Typography variant="h6">{result.netPerformancePercentage.toFixed(6)}%</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Events</Typography>
                  <Typography variant="body2">
                    <Chip label={`${result.summary.depositsCount} deposits`} size="small" sx={{ mr: 0.5 }} />
                    <Chip label={`${result.summary.withdrawalsCount} withdrawals`} size="small" />
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Balance Breakdown
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Day</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell align="right"><strong>Opening</strong></TableCell>
                      <TableCell align="right"><strong>%</strong></TableCell>
                      <TableCell align="right"><strong>Profit</strong></TableCell>
                      <TableCell align="right"><strong>Closing</strong></TableCell>
                      <TableCell><strong>Events</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.dailyBalances.map((day, index) => {
                      const hasEvents = day.deposits.length > 0 || day.withdrawals.length > 0;
                      return (
                        <TableRow key={index} sx={{ backgroundColor: hasEvents ? '#fff3e0' : 'inherit' }}>
                          <TableCell>{day.dayNumber}</TableCell>
                          <TableCell>{dayjs(day.date).format('MMM DD')}</TableCell>
                          <TableCell align="right">${day.openingBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell align="right">{day.percentage.toFixed(4)}%</TableCell>
                          <TableCell align="right" sx={{ color: day.profit >= 0 ? 'success.main' : 'error.main' }}>
                            ${day.profit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="right">${day.closingBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell>
                            {day.deposits.map((d, i) => (
                              <Chip key={i} label={`+$${d.amount.toFixed(2)}`} size="small" color="success" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                            {day.withdrawals.map((w, i) => (
                              <Chip key={i} label={`-$${w.amount.toFixed(2)}`} size="small" color="error" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default PerformancePreview;

